import { useMemo } from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Clock, CheckCircle, AlertTriangle } from 'lucide-react';
import { getISOWeek, getISOWeekYear, startOfISOWeek, addDays, format } from 'date-fns';
import { fr } from 'date-fns/locale';

/* ------------------------------------------------------------------ */
/* Calculs partages (page Planning + formulaire)                       */
/* ------------------------------------------------------------------ */

export const heuresContratDe = (employesDetail, prenom) => {
  const e = (employesDetail || []).find(x => (x.prenom || '').trim() === (prenom || '').trim());
  const h = parseFloat(e?.heures_contrat);
  return Number.isFinite(h) ? h : 0;
};

export const semaineIsoDe = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return null;
  return { semaine: getISOWeek(d), annee: getISOWeekYear(d) };
};

/** Heures deja planifiees par employe pour une semaine ISO donnee (hors entree exclue). */
export const heuresPlanifiees = (planning, employe, semaine, excludeId = null) =>
  (planning || [])
    .filter(p => p.employe === employe && p.semaine_iso === semaine && p.id !== excludeId)
    .reduce((s, p) => s + (parseFloat(p.total) || 0), 0);

const fmtH = (h) => {
  const v = Math.round((h || 0) * 10) / 10;
  return `${v % 1 === 0 ? v.toFixed(0) : v.toFixed(1)}h`;
};

const libelleSemaine = (semaine, annee) => {
  try {
    // Lundi de la semaine ISO demandee : on part du 4 janvier (toujours en semaine 1)
    const base = startOfISOWeek(new Date(annee || new Date().getFullYear(), 0, 4));
    const lundi = addDays(base, (semaine - 1) * 7);
    return `${format(lundi, 'd MMM', { locale: fr })} au ${format(addDays(lundi, 6), 'd MMM', { locale: fr })}`;
  } catch { return ''; }
};

/* ------------------------------------------------------------------ */
/* Ligne de resume par employe                                         */
/* ------------------------------------------------------------------ */

function ResteBadge({ reste, contrat }) {
  if (!contrat) return <Badge variant="outline" className="text-[10px] text-muted-foreground">contrat non renseigne</Badge>;
  if (reste > 0.05) return <Badge variant="outline" className="text-xs gap-1 bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-950 dark:text-amber-300"><Clock className="w-3 h-3" /> reste {fmtH(reste)}</Badge>;
  if (reste < -0.05) return <Badge variant="outline" className="text-xs gap-1 bg-red-100 text-red-700 border-red-300 dark:bg-red-950 dark:text-red-300"><AlertTriangle className="w-3 h-3" /> depasse de {fmtH(-reste)}</Badge>;
  return <Badge variant="outline" className="text-xs gap-1 bg-green-100 text-green-700 border-green-300 dark:bg-green-950 dark:text-green-300"><CheckCircle className="w-3 h-3" /> complet</Badge>;
}

function Jauge({ planifie, contrat }) {
  const pct = contrat > 0 ? Math.min((planifie / contrat) * 100, 100) : 0;
  const over = contrat > 0 && planifie > contrat + 0.05;
  return (
    <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
      <div className={`h-full rounded-full transition-all ${over ? 'bg-red-500' : pct >= 99.5 ? 'bg-green-500' : 'bg-primary'}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Panneau principal                                                   */
/* ------------------------------------------------------------------ */

/**
 * Affiche, au fur et a mesure de la saisie, les heures restant a positionner par employe.
 * - semaine choisie : une carte par employe (contrat / planifie / reste, jauge).
 * - sinon : tableau employes x semaines (du mois choisi, ou semaines ayant du planning).
 */
export function HeuresRestantes({ planning, employesDetail, semaine, month, onSelectSemaine }) {
  const employes = useMemo(() => {
    const list = (employesDetail || [])
      .map(e => ({ prenom: (e.prenom || '').trim(), contrat: parseFloat(e.heures_contrat) || 0, couleur: e.couleur, poste: e.poste }))
      .filter(e => e.prenom);
    // Employes presents dans le planning mais absents de la fiche RH
    const connus = new Set(list.map(e => e.prenom));
    (planning || []).forEach(p => { if (p.employe && !connus.has(p.employe)) { connus.add(p.employe); list.push({ prenom: p.employe, contrat: 0 }); } });
    return list.sort((a, b) => a.prenom.localeCompare(b.prenom));
  }, [employesDetail, planning]);

  const anneeCourante = new Date().getFullYear();

  // Vue semaine
  if (semaine) {
    const totalContrat = employes.reduce((s, e) => s + e.contrat, 0);
    const totalPlanifie = employes.reduce((s, e) => s + heuresPlanifiees(planning, e.prenom, semaine), 0);
    return (
      <Card className="print:hidden" data-testid="heures-restantes">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-sm font-semibold flex items-center gap-2"><Clock className="w-4 h-4 text-primary" /> Heures a positionner, semaine {semaine} <span className="text-xs font-normal text-muted-foreground">({libelleSemaine(semaine, anneeCourante)})</span></p>
            <p className="text-xs text-muted-foreground">Planifie {fmtH(totalPlanifie)} sur {fmtH(totalContrat)} de contrats, reste <span className={`font-semibold ${totalContrat - totalPlanifie > 0.05 ? 'text-amber-700' : 'text-green-700'}`}>{fmtH(Math.max(totalContrat - totalPlanifie, 0))}</span></p>
          </div>
          {employes.length === 0 ? (
            <p className="text-xs text-muted-foreground">Aucun employe : renseigne les fiches dans le module RH (heures de contrat par semaine).</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {employes.map(e => {
                const planifie = heuresPlanifiees(planning, e.prenom, semaine);
                const reste = e.contrat - planifie;
                return (
                  <div key={e.prenom} className="rounded-lg border p-3 space-y-2 bg-card" data-testid={`reste-${e.prenom}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: e.couleur || '#94a3b8' }} />
                        <span className="font-medium text-sm truncate">{e.prenom}</span>
                        {e.poste && <span className="text-[10px] text-muted-foreground truncate">{e.poste}</span>}
                      </div>
                      <ResteBadge reste={reste} contrat={e.contrat} />
                    </div>
                    <Jauge planifie={planifie} contrat={e.contrat} />
                    <p className="text-xs text-muted-foreground tabular-nums">{fmtH(planifie)} planifiees{e.contrat ? ` / ${fmtH(e.contrat)} de contrat` : ''}</p>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Vue multi-semaines : semaines du mois choisi, sinon semaines ayant du planning + semaine courante
  const semaines = (() => {
    const set = new Set();
    if (month) {
      const annee = anneeCourante;
      for (let d = new Date(annee, month - 1, 1); d.getMonth() === month - 1; d = addDays(d, 1)) set.add(getISOWeek(d));
    } else {
      (planning || []).forEach(p => { if (p.semaine_iso) set.add(p.semaine_iso); });
      set.add(getISOWeek(new Date()));
    }
    return [...set].sort((a, b) => a - b);
  })();

  if (employes.length === 0) return null;

  return (
    <Card className="print:hidden" data-testid="heures-restantes">
      <CardContent className="p-0">
        <div className="px-4 pt-3 pb-2 flex items-center justify-between flex-wrap gap-2">
          <p className="text-sm font-semibold flex items-center gap-2"><Clock className="w-4 h-4 text-primary" /> Heures restant a positionner par employe et par semaine</p>
          <p className="text-xs text-muted-foreground">Contrat hebdo moins heures planifiees. Clique sur une semaine pour la filtrer.</p>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Employe</TableHead>
                <TableHead className="text-right">Contrat</TableHead>
                {semaines.map(s => (
                  <TableHead key={s} className="text-center cursor-pointer hover:text-primary" onClick={() => onSelectSemaine && onSelectSemaine(s)} title={libelleSemaine(s, anneeCourante)}>
                    S{s}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {employes.map(e => (
                <TableRow key={e.prenom}>
                  <TableCell className="font-medium">
                    <span className="inline-block w-2.5 h-2.5 rounded-full mr-2 align-middle" style={{ backgroundColor: e.couleur || '#94a3b8' }} />
                    {e.prenom}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">{e.contrat ? fmtH(e.contrat) : '--'}</TableCell>
                  {semaines.map(s => {
                    const planifie = heuresPlanifiees(planning, e.prenom, s);
                    const reste = e.contrat - planifie;
                    let cls = 'text-muted-foreground';
                    let txt = '--';
                    if (e.contrat) {
                      if (planifie === 0) { cls = 'text-amber-700 dark:text-amber-400'; txt = fmtH(reste); }
                      else if (reste > 0.05) { cls = 'text-amber-700 dark:text-amber-400 font-medium'; txt = fmtH(reste); }
                      else if (reste < -0.05) { cls = 'text-red-600 dark:text-red-400 font-semibold'; txt = `+${fmtH(-reste)}`; }
                      else { cls = 'text-green-600 dark:text-green-400 font-medium'; txt = 'OK'; }
                    } else if (planifie > 0) { txt = fmtH(planifie); }
                    return (
                      <TableCell key={s} className={`text-center tabular-nums text-sm ${cls}`} title={`${fmtH(planifie)} planifiees`}>{txt}</TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <p className="px-4 py-2 text-[11px] text-muted-foreground">Orange : heures encore a placer. Vert : semaine complete. Rouge : au-dela du contrat.</p>
      </CardContent>
    </Card>
  );
}
